import { Autocomplete, Button } from "src/components";
import crossIcon from "src/assets/cross.svg";
import { useToast } from "src/hooks/useToast";
import { useEffect, useRef, useState } from "react";
import MultiAutocompleteAsync from "src/components/ui/MultiAutocompleteAsync";
import { getCompanyList, getDivisionList, getStateList } from "src/services/order-api";
import { generateCounterProductReport, getCounterDetails, getProductDetails } from "src/services/reports-api";
import usePermission from "src/hooks/usePermission";
import { fineGrainedPermissionsKeys } from "src/constants/permission";
import moment from "moment";

const ProductReportFilters = ({ onCloseModal, onSuccess }: { onCloseModal: () => void; onSuccess: () => void }) => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<any>(null);
  const [selectedStates, setSelectedStates] = useState<any>([]);
  const [selectedDivisions, setSelectedDivisions] = useState<any>([]);
  const [companyData, setCompanyData] = useState<any>([]);
  const [stateData, setStateData] = useState<any>([]);
  const [divisionData, setDivisionData] = useState<any>([]);
  const [selectedReportType, setSelectedReportType] = useState<any>([]);
  const [selectedModuleType, setSelectedModuleType] = useState<any>([]);
  const [selectedProducts, setSelectedProducts] = useState<any>([]);
  const [selectedCounters, setSelectedCounters] = useState<any>([]);
  const [productData, setProductData] = useState<any>([]);
  const [counterData, setCounterData] = useState<any>([]);
  const [transactionDate, setTransactionDate] = useState<string>("");
  const [revokeStartDate, setRevokeStartDate] = useState<string>("");
  const [revokeEndDate, setRevokeEndDate] = useState<string>("");
  const [productChangesStartDate, setProductChangesStartDate] = useState<string>("");
  const [productChangesEndDate, setProductChangesEndDate] = useState<string>("");
  const [secondLocStartDate, setSecondLocStartDate] = useState<string>("");
  const [secondLocEndDate, setSecondLocEndDate] = useState<string>("");
  const [userAttachmentStartDate, setUserAttachmentStartDate] = useState<string>("");
  const [userAttachmentEndDate, setUserAttachmentEndDate] = useState<string>("");
  const [editDetailsStartDate, setEditDetailsStartDate] = useState<string>("");
  const [editDetailsEndDate, setEditDetailsEndDate] = useState<string>("");
  const [counterVisitStartDate, setCounterVisitStartDate] = useState<string>("");
  const [counterVisitEndDate, setCounterVisitEndDate] = useState<string>("");
  const [rcApiGlenmarkStartDate, setRcApiGlenmarkStartDate] = useState<string>("");
  const [rcApiGlenmarkEndDate, setRcApiGlenmarkEndDate] = useState<string>("");
  const [additionalAttachmentStartDate, setAdditionalAttachmentStartDate] = useState<string>("");
  const [additionalAttachmentEndDate, setAdditionalAttachmentEndDate] = useState<string>("");
  const [showMonthPicker, setShowMonthPicker] = useState(false);
  const monthPickerRef = useRef<HTMLDivElement>(null);

  const { hasPermission: hasCounterProductWithSkuReportAccess, loading: isCounterProductWithSkuPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.REPORTS.COUNTERPRODUCTWITHSKU.module,
    action: fineGrainedPermissionsKeys.REPORTS.COUNTERPRODUCTWITHSKU.action,
  });

  const { hasPermission: hasCounterProductWithoutSkuReportAccess, loading: isCounterProductWithoutSkuPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.REPORTS.COUNTERPRODUCTWITHOUTSKU.module,
    action: fineGrainedPermissionsKeys.REPORTS.COUNTERPRODUCTWITHOUTSKU.action,
  });

  const { hasPermission: hasCounterStockistReportAccess, loading: isCounterStockistPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.REPORTS.COUNTERSTOCKIST.module,
    action: fineGrainedPermissionsKeys.REPORTS.COUNTERSTOCKIST.action,
  });

  const { hasPermission: hasCounterOrdercapSummaryReportAccess, loading: isCounterOrdercapSummaryPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.REPORTS.COUNTERORDERCAPSUMMARY.module,
    action: fineGrainedPermissionsKeys.REPORTS.COUNTERORDERCAPSUMMARY.action,
  });

  const { hasPermission: hasCounterBlockReportAccess, loading: isCounterBlockReportPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.REPORTS.COUNTERBLOCKREPORT.module,
    action: fineGrainedPermissionsKeys.REPORTS.COUNTERBLOCKREPORT.action,
  });

  const { hasPermission: hasCounterRevokeReportAccess, loading: isCounterRevokeReportPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.REPORTS.COUNTERREVOKEREPORT.module,
    action: fineGrainedPermissionsKeys.REPORTS.COUNTERREVOKEREPORT.action,
  });

  const { hasPermission: hasCounterProductChangesReportAccess, loading: isCounterProductChangesPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.REPORTS.COUNTERPRODUCTCHANGES.module,
    action: fineGrainedPermissionsKeys.REPORTS.COUNTERPRODUCTCHANGES.action,
  });

  const { hasPermission: hasCounter2ndLocReportAccess, loading: isCounter2ndLocPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.REPORTS.COUNTER2NDLOC.module,
    action: fineGrainedPermissionsKeys.REPORTS.COUNTER2NDLOC.action,
  });

  const { hasPermission: hasCounterWiseAttachmentCntReportAccess, loading: isCounterWiseAttachmentCntPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.REPORTS.COUNTERWISEATTACHMENTCNT.module,
    action: fineGrainedPermissionsKeys.REPORTS.COUNTERWISEATTACHMENTCNT.action,
  });

  const { hasPermission: hasUserWiseAttachmentCntReportAccess, loading: isUserWiseAttachmentCntPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.REPORTS.USERWISEATTACHMENTCNT.module,
    action: fineGrainedPermissionsKeys.REPORTS.USERWISEATTACHMENTCNT.action,
  });

  const { hasPermission: hasCounterEditDetailsReportAccess, loading: isCounterEditDetailsPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.REPORTS.COUNTEREDITDETAILS.module,
    action: fineGrainedPermissionsKeys.REPORTS.COUNTEREDITDETAILS.action,
  });

  const { hasPermission: hasCounterVisitReportAccess, loading: isCounterVisitPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.REPORTS.COUNTERVISIT.module,
    action: fineGrainedPermissionsKeys.REPORTS.COUNTERVISIT.action,
  });

  const { hasPermission: hasCounterAdditionalAttachmentReportAccess, loading: isCounterAdditionalAttachmentPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.REPORTS.COUNTERADDITIONALATTACHMENT.module,
    action: fineGrainedPermissionsKeys.REPORTS.COUNTERADDITIONALATTACHMENT.action,
  });

  const { hasPermission: hasRcApiReportGlenmarkAccess, loading: isRcApiReportGlenmarkPermissionLoading } = usePermission({
    module: fineGrainedPermissionsKeys.REPORTS.RCAPIREPORTGLENMARK.module,
    action: fineGrainedPermissionsKeys.REPORTS.RCAPIREPORTGLENMARK.action,
  });

  const reportTypeSuggestions = [
    {
      id: "COUNTER_PRODUCT_WITH_SKU",
      name: "Counter Master with SKU",
      condition: !isCounterProductWithSkuPermissionLoading && hasCounterProductWithSkuReportAccess,
    },
    {
      id: "COUNTER_PRODUCT_WITHOUT_SKU",
      name: "Counter Master without SKU",
      condition: !isCounterProductWithoutSkuPermissionLoading && hasCounterProductWithoutSkuReportAccess,
    },
    {
      id: "COUNTER_STOCKIST",
      name: "Counter Stockist",
      condition: !isCounterStockistPermissionLoading && hasCounterStockistReportAccess,
    },
    {
      id: "COUNTER_ORDERCAP_SUMMARY",
      name: "Counter Value Cap Summary",
      condition: !isCounterOrdercapSummaryPermissionLoading && hasCounterOrdercapSummaryReportAccess,
    },
    {
      id: "COUNTER_BLOCK_REPORT",
      name: "Counter Block Report",
      condition: !isCounterBlockReportPermissionLoading && hasCounterBlockReportAccess,
    },
    {
      id: "COUNTER_REVOKE_REPORT",
      name: "Counter Revoke Report",
      condition: !isCounterRevokeReportPermissionLoading && hasCounterRevokeReportAccess,
    },
    {
      id: "COUNTER_PRODUCT_CHANGES",
      name: "Counter Product Changes",
      condition: !isCounterProductChangesPermissionLoading && hasCounterProductChangesReportAccess,
    },
    {
      id: "COUNTER_2NDLOC",
      name: "Counter 2nd Location Report",
      condition: !isCounter2ndLocPermissionLoading && hasCounter2ndLocReportAccess,
    },
    {
      id: "COUNTER_WISE_ATTACHMENT_CNT",
      name: "Counter Wise Attachment Count",
      condition: !isCounterWiseAttachmentCntPermissionLoading && hasCounterWiseAttachmentCntReportAccess,
    },
    {
      id: "USER_WISE_ATTACHMENT_CNT",
      name: "User Wise Attachment Count",
      condition: !isUserWiseAttachmentCntPermissionLoading && hasUserWiseAttachmentCntReportAccess,
    },
    {
      id: "COUNTER_EDIT_DETAILS",
      name: "Counter Edit Details",
      condition: !isCounterEditDetailsPermissionLoading && hasCounterEditDetailsReportAccess,
    },
    {
      id: "COUNTER_VISIT",
      name: "Counter Visit",
      condition: !isCounterVisitPermissionLoading && hasCounterVisitReportAccess,
    },
    {
      id: "COUNTER_ADDITIONAL_ATTACHMENT",
      name: "Counter Additional Attachment",
      condition: !isCounterAdditionalAttachmentPermissionLoading && hasCounterAdditionalAttachmentReportAccess,
    },
    {
      id: "RC_API_REPORT_GLENMARK",
      name: "RC API Report Glenmark",
      condition: !isRcApiReportGlenmarkPermissionLoading && hasRcApiReportGlenmarkAccess,
    },
  ];

  const filteredSuggestions = reportTypeSuggestions.filter((report) => report.condition);

  // Per-report-type override; any report type not listed here defaults to mandatory.
  const COMPANY_NOT_REQUIRED_REPORT_TYPES: string[] = [
    "COUNTER_2NDLOC",
    "COUNTER_WISE_ATTACHMENT_CNT",
    "USER_WISE_ATTACHMENT_CNT",
    "COUNTER_EDIT_DETAILS",
    "COUNTER_VISIT",
    "COUNTER_ADDITIONAL_ATTACHMENT",
    "RC_API_REPORT_GLENMARK",
  ];
  const isCompanyRequired = !COMPANY_NOT_REQUIRED_REPORT_TYPES.includes(selectedReportType?.id);

  const fetchDivision = async (value: any) => {
    const payload = {
      company_code: selectedCompany?.code,
      search: value,
    };

    try {
      const res = await getDivisionList(payload);
      setDivisionData(res?.data?.data);
      return res?.data?.data;
    } catch (error) {
      console.log(error);
    }
  };

  const fetchProduct = async (value: any) => {
    const payload = {
      company_id: selectedCompany?.code,
      division_codes: selectedDivisions?.map((item: any) => item?.company_divisioncode)?.join(","),
      search: value,
    };

    try {
      const res = await getProductDetails(payload);
      setProductData(res?.data);
      return res?.data;
    } catch (error) {
      console.log(error);
    }
  };

  const fetchCounter = async (value: any) => {
    const payload = {
      company_id: selectedCompany?.code,
      counter_code: value,
    };

    try {
      const res = await getCounterDetails(payload);
      setCounterData(res?.data?.data);
      return res?.data?.data;
    } catch (error) {
      console.log(error);
    }
  };

  const handleReportSubmit = async () => {
    if (!selectedReportType?.id) {
      toast({ description: "Please select report type", variant: "destructive" });
      return;
    }

    if (isCompanyRequired && !selectedCompany?.code) {
      toast({ description: "Please select company", variant: "destructive" });
      return;
    }

    // Division is required for with-SKU reports only when neither Product nor Counter is selected
    // (Division field is hidden when Product or Counter is chosen)
    if (
      selectedReportType?.id === "COUNTER_PRODUCT_WITH_SKU" &&
      selectedProducts?.length === 0 &&
      selectedCounters?.length === 0 &&
      selectedDivisions?.length !== 1
    ) {
      toast({ description: "Please select one division for with sku report", variant: "destructive" });
      return;
    }

    if (
      selectedReportType?.id !== "COUNTER_STOCKIST" &&
      selectedReportType?.id !== "COUNTER_ORDERCAP_SUMMARY" &&
      selectedReportType?.id !== "COUNTER_BLOCK_REPORT" &&
      selectedReportType?.id !== "COUNTER_REVOKE_REPORT" &&
      selectedReportType?.id !== "COUNTER_PRODUCT_CHANGES" &&
      selectedReportType?.id !== "COUNTER_2NDLOC" &&
      selectedReportType?.id !== "COUNTER_WISE_ATTACHMENT_CNT" &&
      selectedReportType?.id !== "USER_WISE_ATTACHMENT_CNT" &&
      selectedReportType?.id !== "COUNTER_EDIT_DETAILS" &&
      selectedReportType?.id !== "COUNTER_VISIT" &&
      selectedReportType?.id !== "COUNTER_ADDITIONAL_ATTACHMENT" &&
      selectedReportType?.id !== "RC_API_REPORT_GLENMARK" &&
      !selectedModuleType?.id
    ) {
      //M10-13117
      toast({ description: "Please select module type", variant: "destructive" });
      return;
    }

    if (selectedReportType?.id === "COUNTER_ORDERCAP_SUMMARY") {
      if (!transactionDate) {
        toast({ description: "Please select transaction date", variant: "destructive" });
        return;
      }
      if (moment(transactionDate).isSameOrAfter(moment().startOf("month"))) {
        toast({ description: "Current month is not allowed. Please select an earlier month.", variant: "destructive" });
        return;
      }
    }

    if (selectedReportType?.id === "COUNTER_REVOKE_REPORT") {
      if (!revokeStartDate || !revokeEndDate) {
        toast({ description: "Please select start date and end date", variant: "destructive" });
        return;
      }
      if (moment(revokeStartDate).isAfter(moment(revokeEndDate))) {
        toast({ description: "Start date cannot be after end date", variant: "destructive" });
        return;
      }
    }

    if (selectedReportType?.id === "COUNTER_PRODUCT_CHANGES") {
      if (!productChangesStartDate || !productChangesEndDate) {
        toast({ description: "Please select start date and end date", variant: "destructive" });
        return;
      }
      if (moment(productChangesStartDate).isAfter(moment(productChangesEndDate))) {
        toast({ description: "Start date cannot be after end date", variant: "destructive" });
        return;
      }
      if (moment.utc(productChangesEndDate).isAfter(moment.utc(productChangesStartDate).add(1, "month").subtract(1, "day"), "day")) {
        toast({ description: "Date range cannot exceed one month", variant: "destructive" });
        return;
      }
    }

    if (selectedReportType?.id === "COUNTER_2NDLOC") {
      if (!secondLocStartDate || !secondLocEndDate) {
        toast({ description: "Please select start date and end date", variant: "destructive" });
        return;
      }
      if (moment(secondLocStartDate).isAfter(moment(secondLocEndDate))) {
        toast({ description: "Start date cannot be after end date", variant: "destructive" });
        return;
      }
    }

    if (selectedReportType?.id === "USER_WISE_ATTACHMENT_CNT") {
      if (!userAttachmentStartDate || !userAttachmentEndDate) {
        toast({ description: "Please select start date and end date", variant: "destructive" });
        return;
      }
      if (moment(userAttachmentStartDate).isAfter(moment(userAttachmentEndDate))) {
        toast({ description: "Start date cannot be after end date", variant: "destructive" });
        return;
      }
    }

    if (selectedReportType?.id === "COUNTER_EDIT_DETAILS") {
      if (!editDetailsStartDate || !editDetailsEndDate) {
        toast({ description: "Please select start date and end date", variant: "destructive" });
        return;
      }
      if (moment(editDetailsStartDate).isAfter(moment(editDetailsEndDate))) {
        toast({ description: "Start date cannot be after end date", variant: "destructive" });
        return;
      }
    }

    if (selectedReportType?.id === "COUNTER_VISIT") {
      if (!counterVisitStartDate || !counterVisitEndDate) {
        toast({ description: "Please select start date and end date", variant: "destructive" });
        return;
      }
      if (moment(counterVisitStartDate).isAfter(moment(counterVisitEndDate))) {
        toast({ description: "Start date cannot be after end date", variant: "destructive" });
        return;
      }
    }

    if (selectedReportType?.id === "COUNTER_ADDITIONAL_ATTACHMENT") {
      if (!additionalAttachmentStartDate || !additionalAttachmentEndDate) {
        toast({ description: "Please select start date and end date", variant: "destructive" });
        return;
      }
      if (moment(additionalAttachmentStartDate).isAfter(moment(additionalAttachmentEndDate))) {
        toast({ description: "Start date cannot be after end date", variant: "destructive" });
        return;
      }
    }

    if (selectedReportType?.id === "RC_API_REPORT_GLENMARK") {
      if (!rcApiGlenmarkStartDate || !rcApiGlenmarkEndDate) {
        toast({ description: "Please select start date and end date", variant: "destructive" });
        return;
      }
      if (moment(rcApiGlenmarkStartDate).isAfter(moment(rcApiGlenmarkEndDate))) {
        toast({ description: "Start date cannot be after end date", variant: "destructive" });
        return;
      }
    }

    if (selectedReportType?.id === "COUNTER_PRODUCT_WITH_SKU" && selectedProducts?.length > 1) {
      toast({ description: "Please select only one product", variant: "destructive" });
      return;
    }

    if (
      (selectedReportType?.id === "COUNTER_PRODUCT_WITH_SKU" || selectedReportType?.id === "COUNTER_PRODUCT_WITHOUT_SKU") &&
      selectedCounters?.length > 10
    ) {
      toast({ description: "Please select up to 10 counters", variant: "destructive" });
      return;
    }

    const exclusiveSelections = [selectedProducts?.length > 0, selectedCounters?.length > 0, selectedDivisions?.length > 0].filter(Boolean).length;
    if (exclusiveSelections > 1) {
      toast({ description: "Product, Counter, and Division are mutually exclusive", variant: "destructive" });
      return;
    }

    setIsLoading(true);
    const filters: any = {
      divisions: selectedDivisions?.map((item: any) => item?.company_divisioncode),
      states: selectedStates?.map((item: any) => item?.state_code),
    };
    if (selectedReportType?.id === "COUNTER_PRODUCT_WITH_SKU") {
      filters.item_codes = selectedProducts?.map((item: any) => item?.item_code);
    }
    if (selectedReportType?.id === "COUNTER_PRODUCT_WITH_SKU" || selectedReportType?.id === "COUNTER_PRODUCT_WITHOUT_SKU") {
      filters.counter_codes = selectedCounters?.map((item: any) => item?.counter_code);
    }
    if (selectedReportType?.id === "COUNTER_ORDERCAP_SUMMARY") {
      filters.transaction_date = transactionDate ? moment(transactionDate).format("YYYY-MM-DD") : "";
    }
    const payload: any = {
      report_type: selectedReportType?.id,
      filters: filters,
      company_code: selectedCompany?.code,
      company_name: selectedCompany?.name,
      module_code: selectedModuleType?.id, //M10-11806
    };
    if (selectedReportType?.id === "COUNTER_REVOKE_REPORT") {
      payload.time_range = revokeStartDate && revokeEndDate ? `${revokeStartDate}-${revokeEndDate}` : "-";
    }
    if (selectedReportType?.id === "COUNTER_PRODUCT_CHANGES") {
      payload.time_range = productChangesStartDate && productChangesEndDate ? `${productChangesStartDate}-${productChangesEndDate}` : "-";
    }
    if (selectedReportType?.id === "COUNTER_2NDLOC") {
      payload.time_range = secondLocStartDate && secondLocEndDate ? `${secondLocStartDate}-${secondLocEndDate}` : "-";
    }
    if (selectedReportType?.id === "USER_WISE_ATTACHMENT_CNT") {
      payload.time_range = userAttachmentStartDate && userAttachmentEndDate ? `${userAttachmentStartDate}-${userAttachmentEndDate}` : "-";
    }
    if (selectedReportType?.id === "COUNTER_EDIT_DETAILS") {
      payload.time_range = editDetailsStartDate && editDetailsEndDate ? `${editDetailsStartDate}-${editDetailsEndDate}` : "-";
    }
    if (selectedReportType?.id === "COUNTER_VISIT") {
      payload.time_range = counterVisitStartDate && counterVisitEndDate ? `${counterVisitStartDate}-${counterVisitEndDate}` : "-";
    }
    if (selectedReportType?.id === "COUNTER_ADDITIONAL_ATTACHMENT") {
      payload.time_range =
        additionalAttachmentStartDate && additionalAttachmentEndDate
          ? `${additionalAttachmentStartDate}-${additionalAttachmentEndDate}`
          : "-";
    }
    if (selectedReportType?.id === "RC_API_REPORT_GLENMARK") {
      // No company selection for this report; drop any company carried over from another report type
      delete payload.company_code;
      delete payload.company_name;
      payload.time_range = rcApiGlenmarkStartDate && rcApiGlenmarkEndDate ? `${rcApiGlenmarkStartDate}-${rcApiGlenmarkEndDate}` : "-";
    }
    try {
      const response = await generateCounterProductReport(payload);
      console.log(response);
      toast({ description: "Report generation request sent successfully", variant: "default" });
      if (response?.data?.success) {
        onCloseModal();
        onSuccess();
      }
    } catch (error: any) {
      console.log(error);
      toast({ description: error?.response?.data?.message || "Failed to generate report", variant: "destructive" });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!companyData?.length) {
      getCompanyList()
        .then((res) => setCompanyData(res?.data))
        .catch((err) => console.log(err));
    }
    if (!stateData?.length) {
      getStateList()
        .then((res) => setStateData(res?.data?.data))
        .catch((err) => console.log(err));
    }
  }, []);

  useEffect(() => {
    if (!showMonthPicker) return;
    const handler = (e: MouseEvent) => {
      if (monthPickerRef.current && !monthPickerRef.current.contains(e.target as Node)) {
        setShowMonthPicker(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showMonthPicker]);

  console.log("selectedModuleType", selectedModuleType);

  // Firefox has no native support for <input type="month"> (renders as a plain text
  // field with no picker), so the month/year are driven by plain <select> dropdowns instead.
  const maxTransactionMoment = moment().subtract(1, "month");
  const maxTransactionYear = maxTransactionMoment.year();
  const maxTransactionMonth = maxTransactionMoment.month() + 1; // 1-12
  const transactionYear = transactionDate ? transactionDate.slice(0, 4) : "";
  const transactionMonth = transactionDate ? transactionDate.slice(5, 7) : "";
  const transactionMonthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const transactionYearOptions = Array.from({ length: 11 }, (_, i) => maxTransactionYear - i);

  const handleTransactionMonthChange = (e: any) => {
    const month = e.target.value;
    if (!month) {
      setTransactionDate("");
      return;
    }
    setTransactionDate(`${transactionYear || maxTransactionYear}-${month}-01`);
  };

  const handleTransactionYearChange = (e: any) => {
    const year = e.target.value;
    if (!year) {
      setTransactionDate("");
      return;
    }
    setTransactionDate(`${year}-${transactionMonth || String(maxTransactionMonth).padStart(2, "0")}-01`);
  };

  return (
    <div className="flex flex-col gap-y-2 w-[400px] mx-auto bg-white p-4 border border-gray-300 shadow-lg rounded-lg">
      <div className="flex justify-between items-center">
        <p className="text-normal py-1 font-semibold text-[#1B1A1F]">Generate Report</p>
        <Button className="bg-transparent -mr-3" onClick={onCloseModal}>
          <img src={crossIcon} alt="cross" className="w-5 h-5 font-bold invert cursor-pointer" />
        </Button>
      </div>
      <div className="flex flex-col gap-y-2 px-2">
        {/* M10-13117 */}
        <div className="mt-0">
          <label className="text-sm font-normal text-[#666666]">Report Type</label>
          <Autocomplete
            placeholder="Report Type"
            inputClassName="w-full"
            suggestionList={filteredSuggestions || []}
            displayKey="name"
            uniqueKey="id"
            defaultValue={selectedReportType}
            onSelectionChange={(value: any) => {
              setSelectedReportType(value);
            }}
          />
        </div>
        {/* M10-13117 */}
        {selectedReportType?.id !== "COUNTER_2NDLOC" &&
          selectedReportType?.id !== "COUNTER_WISE_ATTACHMENT_CNT" &&
          selectedReportType?.id !== "USER_WISE_ATTACHMENT_CNT" &&
          selectedReportType?.id !== "COUNTER_EDIT_DETAILS" &&
          selectedReportType?.id !== "COUNTER_VISIT" &&
          selectedReportType?.id !== "COUNTER_ADDITIONAL_ATTACHMENT" &&
          selectedReportType?.id !== "RC_API_REPORT_GLENMARK" && (
          <div className="mb-2">
            <label className="text-sm font-normal text-[#666666]">
              Company Name {isCompanyRequired && <span className="text-red-500">*</span>}
            </label>
            <Autocomplete
              onSelectionChange={(value: any) => {
                setSelectedCompany(value);
                setSelectedDivisions([]); //M10-13014
                setSelectedProducts([]);
                setSelectedCounters([]);
              }}
              placeholder="Select Company"
              inputClassName="w-full mt-2"
              suggestionList={companyData || []}
              displayKey="name"
              defaultValue={selectedCompany}
              uniqueKey="code"
            />
          </div>
        )}
        {selectedReportType?.id === "COUNTER_ORDERCAP_SUMMARY" && (
          <>
            <div className="mt-0">
              <label className="text-sm font-normal text-[#666666]">Transaction Date</label>
              <div className="flex gap-2">
                <select
                  value={transactionMonth}
                  onChange={handleTransactionMonthChange}
                  className="w-full py-1 border border-gray-300 rounded-lg text-sm px-2 outline-none"
                >
                  <option value="">Month</option>
                  {transactionMonthNames.map((name, idx) => {
                    const monthNum = idx + 1;
                    const disabled = Number(transactionYear || maxTransactionYear) === maxTransactionYear && monthNum > maxTransactionMonth;
                    return (
                      <option key={name} value={String(monthNum).padStart(2, "0")} disabled={disabled}>
                        {name}
                      </option>
                    );
                  })}
                </select>
                <select
                  value={transactionYear}
                  onChange={handleTransactionYearChange}
                  className="w-full py-1 border border-gray-300 rounded-lg text-sm px-2 outline-none"
                >
                  <option value="">Year</option>
                  {transactionYearOptions.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-gray-500 mt-1">Only the 1st of past months can be selected. Current month is not allowed.</p>
            </div>
          </>
        )}
        {selectedReportType?.id === "COUNTER_REVOKE_REPORT" && (
          <>
            <div className="mt-0">
              <label className="text-sm font-normal text-[#666666]">Start Date</label>
              <input
                type="date"
                value={revokeStartDate ? revokeStartDate.split("T")[0] : ""}
                max={revokeEndDate ? revokeEndDate.split("T")[0] : undefined}
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value);
                  const now = new Date();
                  selectedDate.setUTCHours(now.getUTCHours());
                  selectedDate.setUTCMinutes(now.getUTCMinutes());
                  selectedDate.setUTCSeconds(now.getUTCSeconds());
                  selectedDate.setUTCMilliseconds(now.getUTCMilliseconds());
                  setRevokeStartDate(selectedDate.toISOString());
                }}
                className="w-full py-1 border border-gray-300 rounded-lg text-sm px-2 outline-none"
              />
            </div>
            <div className="mt-0">
              <label className="text-sm font-normal text-[#666666]">End Date</label>
              <input
                type="date"
                value={revokeEndDate ? revokeEndDate.split("T")[0] : ""}
                min={revokeStartDate ? revokeStartDate.split("T")[0] : undefined}
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value);
                  const now = new Date();
                  selectedDate.setUTCHours(now.getUTCHours());
                  selectedDate.setUTCMinutes(now.getUTCMinutes());
                  selectedDate.setUTCSeconds(now.getUTCSeconds());
                  selectedDate.setUTCMilliseconds(now.getUTCMilliseconds());
                  setRevokeEndDate(selectedDate.toISOString());
                }}
                className="w-full py-1 border border-gray-300 rounded-lg text-sm px-2 outline-none"
              />
            </div>
          </>
        )}
        {selectedReportType?.id === "COUNTER_PRODUCT_CHANGES" && (
          <>
            <div className="">
              <label className="text-sm mb-2 font-normal text-[#666666]">Divisions</label>
              <MultiAutocompleteAsync
                placeholder="Divisions"
                inputClassName="w-full"
                suggestionList={divisionData || []}
                displayKey="division_name"
                uniqueKey="company_divisioncode"
                defaultValue={selectedDivisions}
                controlledValue={selectedDivisions}
                isAsyncQuery={true}
                showSelectAll={Boolean(selectedCompany?.code)}
                asyncListFunction={selectedCompany?.code ? fetchDivision : undefined}
                onSelectionChange={(value: any, action: any) => {
                  // actions: 'add' | 'remove' | 'bulk-add' | 'bulk-remove'
                  if (action === "bulk-add") {
                    setSelectedDivisions(Array.isArray(value) ? value : []);
                    return;
                  }
                  if (action === "bulk-remove") {
                    setSelectedDivisions([]);
                    return;
                  }
                  if (action === "remove") {
                    setSelectedDivisions((prev: any[]) => prev.filter((item: any) => item.company_divisioncode !== value.company_divisioncode));
                    return;
                  }
                  setSelectedDivisions((prev: any[]) => [...prev, value]);
                }}
              />
              {selectedDivisions.length > 0 && selectedDivisions.length < divisionData.length && (
                <p className="text-xs text-gray-500 mt-1">Some divisions selected</p>
              )}
            </div>
            <div className="mt-0">
              <label className="text-sm font-normal text-[#666666]">Start Date</label>
              <input
                type="date"
                value={productChangesStartDate ? productChangesStartDate.split("T")[0] : ""}
                max={new Date().toISOString().split("T")[0]}
                onChange={(e) => {
                  const isoDate = moment.utc(e.target.value, "YYYY-MM-DD").startOf("day").toISOString();
                  setProductChangesStartDate(isoDate);
                  if (
                    productChangesEndDate &&
                    (moment.utc(productChangesEndDate).isBefore(moment.utc(isoDate), "day") ||
                      moment.utc(productChangesEndDate).isAfter(moment.utc(isoDate).add(1, "month").subtract(1, "day"), "day"))
                  ) {
                    setProductChangesEndDate("");
                  }
                }}
                className="w-full py-1 border border-gray-300 rounded-lg text-sm px-2 outline-none"
              />
            </div>
            <div className="mt-0">
              <label className="text-sm font-normal text-[#666666]">End Date</label>
              <input
                type="date"
                value={productChangesEndDate ? productChangesEndDate.split("T")[0] : ""}
                min={productChangesStartDate ? productChangesStartDate.split("T")[0] : undefined}
                max={
                  productChangesStartDate
                    ? moment.min(moment.utc(productChangesStartDate).add(1, "month").subtract(1, "day"), moment.utc()).format("YYYY-MM-DD")
                    : new Date().toISOString().split("T")[0]
                }
                onChange={(e) => {
                  const isoDate = moment.utc(e.target.value, "YYYY-MM-DD").startOf("day").toISOString();
                  setProductChangesEndDate(isoDate);
                }}
                className="w-full py-1 border border-gray-300 rounded-lg text-sm px-2 outline-none"
              />
              <p className="text-xs text-gray-500 mt-1">Date range cannot exceed one month.</p>
            </div>
          </>
        )}
        {selectedReportType?.id === "COUNTER_2NDLOC" && (
          <>
            <div className="mt-0">
              <label className="text-sm font-normal text-[#666666]">Start Date</label>
              <input
                type="date"
                value={secondLocStartDate ? secondLocStartDate.split("T")[0] : ""}
                max={secondLocEndDate ? secondLocEndDate.split("T")[0] : undefined}
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value);
                  const now = new Date();
                  selectedDate.setUTCHours(now.getUTCHours());
                  selectedDate.setUTCMinutes(now.getUTCMinutes());
                  selectedDate.setUTCSeconds(now.getUTCSeconds());
                  selectedDate.setUTCMilliseconds(now.getUTCMilliseconds());
                  setSecondLocStartDate(selectedDate.toISOString());
                }}
                className="w-full py-1 border border-gray-300 rounded-lg text-sm px-2 outline-none"
              />
            </div>
            <div className="mt-0">
              <label className="text-sm font-normal text-[#666666]">End Date</label>
              <input
                type="date"
                value={secondLocEndDate ? secondLocEndDate.split("T")[0] : ""}
                min={secondLocStartDate ? secondLocStartDate.split("T")[0] : undefined}
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value);
                  const now = new Date();
                  selectedDate.setUTCHours(now.getUTCHours());
                  selectedDate.setUTCMinutes(now.getUTCMinutes());
                  selectedDate.setUTCSeconds(now.getUTCSeconds());
                  selectedDate.setUTCMilliseconds(now.getUTCMilliseconds());
                  setSecondLocEndDate(selectedDate.toISOString());
                }}
                className="w-full py-1 border border-gray-300 rounded-lg text-sm px-2 outline-none"
              />
            </div>
          </>
        )}
        {selectedReportType?.id === "USER_WISE_ATTACHMENT_CNT" && (
          <>
            <div className="mt-0">
              <label className="text-sm font-normal text-[#666666]">Start Date</label>
              <input
                type="date"
                value={userAttachmentStartDate ? userAttachmentStartDate.split("T")[0] : ""}
                max={userAttachmentEndDate ? userAttachmentEndDate.split("T")[0] : undefined}
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value);
                  const now = new Date();
                  selectedDate.setUTCHours(now.getUTCHours());
                  selectedDate.setUTCMinutes(now.getUTCMinutes());
                  selectedDate.setUTCSeconds(now.getUTCSeconds());
                  selectedDate.setUTCMilliseconds(now.getUTCMilliseconds());
                  setUserAttachmentStartDate(selectedDate.toISOString());
                }}
                className="w-full py-1 border border-gray-300 rounded-lg text-sm px-2 outline-none"
              />
            </div>
            <div className="mt-0">
              <label className="text-sm font-normal text-[#666666]">End Date</label>
              <input
                type="date"
                value={userAttachmentEndDate ? userAttachmentEndDate.split("T")[0] : ""}
                min={userAttachmentStartDate ? userAttachmentStartDate.split("T")[0] : undefined}
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value);
                  const now = new Date();
                  selectedDate.setUTCHours(now.getUTCHours());
                  selectedDate.setUTCMinutes(now.getUTCMinutes());
                  selectedDate.setUTCSeconds(now.getUTCSeconds());
                  selectedDate.setUTCMilliseconds(now.getUTCMilliseconds());
                  setUserAttachmentEndDate(selectedDate.toISOString());
                }}
                className="w-full py-1 border border-gray-300 rounded-lg text-sm px-2 outline-none"
              />
            </div>
          </>
        )}
        {selectedReportType?.id === "COUNTER_EDIT_DETAILS" && (
          <>
            <div className="mt-0">
              <label className="text-sm font-normal text-[#666666]">Start Date</label>
              <input
                type="date"
                value={editDetailsStartDate ? editDetailsStartDate.split("T")[0] : ""}
                max={editDetailsEndDate ? editDetailsEndDate.split("T")[0] : undefined}
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value);
                  const now = new Date();
                  selectedDate.setUTCHours(now.getUTCHours());
                  selectedDate.setUTCMinutes(now.getUTCMinutes());
                  selectedDate.setUTCSeconds(now.getUTCSeconds());
                  selectedDate.setUTCMilliseconds(now.getUTCMilliseconds());
                  setEditDetailsStartDate(selectedDate.toISOString());
                }}
                className="w-full py-1 border border-gray-300 rounded-lg text-sm px-2 outline-none"
              />
            </div>
            <div className="mt-0">
              <label className="text-sm font-normal text-[#666666]">End Date</label>
              <input
                type="date"
                value={editDetailsEndDate ? editDetailsEndDate.split("T")[0] : ""}
                min={editDetailsStartDate ? editDetailsStartDate.split("T")[0] : undefined}
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value);
                  const now = new Date();
                  selectedDate.setUTCHours(now.getUTCHours());
                  selectedDate.setUTCMinutes(now.getUTCMinutes());
                  selectedDate.setUTCSeconds(now.getUTCSeconds());
                  selectedDate.setUTCMilliseconds(now.getUTCMilliseconds());
                  setEditDetailsEndDate(selectedDate.toISOString());
                }}
                className="w-full py-1 border border-gray-300 rounded-lg text-sm px-2 outline-none"
              />
            </div>
          </>
        )}
        {selectedReportType?.id === "COUNTER_VISIT" && (
          <>
            <div className="mt-0">
              <label className="text-sm font-normal text-[#666666]">Start Date</label>
              <input
                type="date"
                value={counterVisitStartDate ? counterVisitStartDate.split("T")[0] : ""}
                max={counterVisitEndDate ? counterVisitEndDate.split("T")[0] : undefined}
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value);
                  const now = new Date();
                  selectedDate.setUTCHours(now.getUTCHours());
                  selectedDate.setUTCMinutes(now.getUTCMinutes());
                  selectedDate.setUTCSeconds(now.getUTCSeconds());
                  selectedDate.setUTCMilliseconds(now.getUTCMilliseconds());
                  setCounterVisitStartDate(selectedDate.toISOString());
                }}
                className="w-full py-1 border border-gray-300 rounded-lg text-sm px-2 outline-none"
              />
            </div>
            <div className="mt-0">
              <label className="text-sm font-normal text-[#666666]">End Date</label>
              <input
                type="date"
                value={counterVisitEndDate ? counterVisitEndDate.split("T")[0] : ""}
                min={counterVisitStartDate ? counterVisitStartDate.split("T")[0] : undefined}
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value);
                  const now = new Date();
                  selectedDate.setUTCHours(now.getUTCHours());
                  selectedDate.setUTCMinutes(now.getUTCMinutes());
                  selectedDate.setUTCSeconds(now.getUTCSeconds());
                  selectedDate.setUTCMilliseconds(now.getUTCMilliseconds());
                  setCounterVisitEndDate(selectedDate.toISOString());
                }}
                className="w-full py-1 border border-gray-300 rounded-lg text-sm px-2 outline-none"
              />
            </div>
          </>
        )}
        {selectedReportType?.id === "RC_API_REPORT_GLENMARK" && (
          <>
            <div className="mt-0">
              <label className="text-sm font-normal text-[#666666]">Start Date</label>
              <input
                type="date"
                value={rcApiGlenmarkStartDate ? rcApiGlenmarkStartDate.split("T")[0] : ""}
                max={rcApiGlenmarkEndDate ? rcApiGlenmarkEndDate.split("T")[0] : undefined}
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value);
                  const now = new Date();
                  selectedDate.setUTCHours(now.getUTCHours());
                  selectedDate.setUTCMinutes(now.getUTCMinutes());
                  selectedDate.setUTCSeconds(now.getUTCSeconds());
                  selectedDate.setUTCMilliseconds(now.getUTCMilliseconds());
                  setRcApiGlenmarkStartDate(selectedDate.toISOString());
                }}
                className="w-full py-1 border border-gray-300 rounded-lg text-sm px-2 outline-none"
              />
            </div>
            <div className="mt-0">
              <label className="text-sm font-normal text-[#666666]">End Date</label>
              <input
                type="date"
                value={rcApiGlenmarkEndDate ? rcApiGlenmarkEndDate.split("T")[0] : ""}
                min={rcApiGlenmarkStartDate ? rcApiGlenmarkStartDate.split("T")[0] : undefined}
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value);
                  const now = new Date();
                  selectedDate.setUTCHours(now.getUTCHours());
                  selectedDate.setUTCMinutes(now.getUTCMinutes());
                  selectedDate.setUTCSeconds(now.getUTCSeconds());
                  selectedDate.setUTCMilliseconds(now.getUTCMilliseconds());
                  setRcApiGlenmarkEndDate(selectedDate.toISOString());
                }}
                className="w-full py-1 border border-gray-300 rounded-lg text-sm px-2 outline-none"
              />
            </div>
          </>
        )}
        {selectedReportType?.id === "COUNTER_ADDITIONAL_ATTACHMENT" && (
          <>
            <div className="mt-0">
              <label className="text-sm font-normal text-[#666666]">Start Date</label>
              <input
                type="date"
                value={additionalAttachmentStartDate ? additionalAttachmentStartDate.split("T")[0] : ""}
                max={additionalAttachmentEndDate ? additionalAttachmentEndDate.split("T")[0] : undefined}
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value);
                  const now = new Date();
                  selectedDate.setUTCHours(now.getUTCHours());
                  selectedDate.setUTCMinutes(now.getUTCMinutes());
                  selectedDate.setUTCSeconds(now.getUTCSeconds());
                  selectedDate.setUTCMilliseconds(now.getUTCMilliseconds());
                  setAdditionalAttachmentStartDate(selectedDate.toISOString());
                }}
                className="w-full py-1 border border-gray-300 rounded-lg text-sm px-2 outline-none"
              />
            </div>
            <div className="mt-0">
              <label className="text-sm font-normal text-[#666666]">End Date</label>
              <input
                type="date"
                value={additionalAttachmentEndDate ? additionalAttachmentEndDate.split("T")[0] : ""}
                min={additionalAttachmentStartDate ? additionalAttachmentStartDate.split("T")[0] : undefined}
                onChange={(e) => {
                  const selectedDate = new Date(e.target.value);
                  const now = new Date();
                  selectedDate.setUTCHours(now.getUTCHours());
                  selectedDate.setUTCMinutes(now.getUTCMinutes());
                  selectedDate.setUTCSeconds(now.getUTCSeconds());
                  selectedDate.setUTCMilliseconds(now.getUTCMilliseconds());
                  setAdditionalAttachmentEndDate(selectedDate.toISOString());
                }}
                className="w-full py-1 border border-gray-300 rounded-lg text-sm px-2 outline-none"
              />
            </div>
          </>
        )}
        {selectedReportType?.id !== "COUNTER_STOCKIST" &&
          selectedReportType?.id !== "COUNTER_ORDERCAP_SUMMARY" &&
          selectedReportType?.id !== "COUNTER_BLOCK_REPORT" &&
          selectedReportType?.id !== "COUNTER_REVOKE_REPORT" &&
          selectedReportType?.id !== "COUNTER_PRODUCT_CHANGES" &&
          selectedReportType?.id !== "COUNTER_2NDLOC" &&
          selectedReportType?.id !== "COUNTER_WISE_ATTACHMENT_CNT" &&
          selectedReportType?.id !== "USER_WISE_ATTACHMENT_CNT" &&
          selectedReportType?.id !== "COUNTER_EDIT_DETAILS" &&
          selectedReportType?.id !== "COUNTER_VISIT" &&
          selectedReportType?.id !== "COUNTER_ADDITIONAL_ATTACHMENT" &&
          selectedReportType?.id !== "RC_API_REPORT_GLENMARK" && (
            <>
              {selectedReportType?.id === "COUNTER_PRODUCT_WITH_SKU" && selectedCounters?.length === 0 && selectedDivisions?.length === 0 && (
                <div className="mt-0">
                  <label className="text-sm font-normal text-[#666666]">Products</label>
                  <MultiAutocompleteAsync
                    placeholder="Products"
                    inputClassName="w-full"
                    suggestionList={productData || []}
                    displayKey="name"
                    uniqueKey="item_code"
                    defaultValue={selectedProducts}
                    controlledValue={selectedProducts}
                    isAsyncQuery={true}
                    asyncListFunction={selectedCompany?.code ? fetchProduct : undefined}
                    onSelectionChange={(value: any, action: any) => {
                      if (action === "remove") {
                        setSelectedProducts(selectedProducts.filter((item: any) => item.item_code !== value.item_code));
                      } else {
                        // Product, Counter, Division are mutually exclusive
                        setSelectedProducts([...selectedProducts, value]);
                        setSelectedCounters([]);
                        setSelectedDivisions([]);
                      }
                    }}
                    showSecondaryDisplayKey={true}
                    secondaryDisplayKey="item_code"
                    additionalDisplayKey={true}
                    additionalDisplayKeySearch="item_code"
                  />
                </div>
              )}
              {selectedProducts?.length === 0 && selectedDivisions?.length === 0 && (
                <div className="mt-0">
                  <label className="text-sm font-normal text-[#666666]">Counters</label>
                  <MultiAutocompleteAsync
                    placeholder="Counters"
                    inputClassName="w-full"
                    suggestionList={counterData || []}
                    displayKey="counter_name"
                    uniqueKey="counter_code"
                    defaultValue={selectedCounters}
                    controlledValue={selectedCounters}
                    isAsyncQuery={true}
                    asyncListFunction={selectedCompany?.code ? fetchCounter : undefined}
                    additionalDisplayKey={true}
                    additionalDisplayKeySearch="counter_code"
                    showSecondaryDisplayKey={true}
                    secondaryDisplayKey="counter_code"
                    onSelectionChange={(value: any, action: any) => {
                      if (action === "remove") {
                        setSelectedCounters(selectedCounters.filter((item: any) => item.counter_code !== value.counter_code));
                      } else {
                        if (selectedCounters?.length >= 10) {
                          toast({ description: "You can select a maximum of 10 counters", variant: "destructive" });
                          // New array reference forces MultiAutocompleteAsync to resync and drop the 11th item
                          setSelectedCounters([...selectedCounters]);
                          return;
                        }
                        // Product, Counter, Division are mutually exclusive
                        setSelectedCounters([...selectedCounters, value]);
                        setSelectedProducts([]);
                        setSelectedDivisions([]);
                      }
                    }}
                  />
                </div>
              )}
              {selectedProducts?.length === 0 && selectedCounters?.length === 0 && (
                <div className="">
                  <label className="text-sm mb-2 font-normal text-[#666666]">Divisions</label>
                  <MultiAutocompleteAsync
                    placeholder="Divisions"
                    inputClassName="w-full"
                    suggestionList={divisionData || []}
                    displayKey="division_name"
                    uniqueKey="company_divisioncode"
                    defaultValue={selectedDivisions}
                    controlledValue={selectedDivisions}
                    isAsyncQuery={true}
                    showSelectAll={Boolean(selectedCompany?.code)}
                    asyncListFunction={selectedCompany?.code ? fetchDivision : undefined}
                    onSelectionChange={(value: any, action: any) => {
                      // actions: 'add' | 'remove' | 'bulk-add' | 'bulk-remove'
                      if (action === "bulk-add") {
                        // Product, Counter, Division are mutually exclusive
                        setSelectedDivisions(Array.isArray(value) ? value : []);
                        setSelectedProducts([]);
                        setSelectedCounters([]);
                        return;
                      }
                      if (action === "bulk-remove") {
                        setSelectedDivisions([]);
                        return;
                      }
                      if (action === "remove") {
                        setSelectedDivisions((prev: any[]) => prev.filter((item: any) => item.company_divisioncode !== value.company_divisioncode));
                        return;
                      }
                      // Product, Counter, Division are mutually exclusive
                      setSelectedDivisions((prev: any[]) => [...prev, value]);
                      setSelectedProducts([]);
                      setSelectedCounters([]);
                    }}
                  />
                  {selectedDivisions.length > 0 && selectedDivisions.length < divisionData.length && (
                    <p className="text-xs text-gray-500 mt-1">Some divisions selected</p>
                  )}
                </div>
              )}
              <div className="mt-0">
                <label className="text-sm font-normal text-[#666666]">States</label>
                <MultiAutocompleteAsync
                  placeholder="States"
                  inputClassName="w-full"
                  suggestionList={stateData || []}
                  controlledValue={selectedStates}
                  displayKey="state_name"
                  uniqueKey="state_code"
                  defaultValue={selectedStates}
                  onSelectionChange={(value: any, action: any) => {
                    if (action === "remove") {
                      setSelectedStates(selectedStates.filter((item: any) => item.state_code !== value.state_code));
                    } else {
                      setSelectedStates([...selectedStates, value]);
                    }
                  }}
                />
              </div>

              <div className="mt-0">
                <label className="text-sm font-normal text-[#666666]">Module Type</label>
                <Autocomplete
                  placeholder="Module Type"
                  inputClassName="w-full"
                  suggestionList={[
                    {
                      id: "1",
                      name: "Medvol",
                    },
                    {
                      id: "2",
                      name: "POB",
                    },
                    {
                      id: "3",
                      name: "PUC",
                    },
                    {
                      id: "4",
                      name: "KAM",
                    },
                  ]}
                  displayKey="name"
                  uniqueKey="id"
                  defaultValue={selectedModuleType}
                  onSelectionChange={(value: any) => {
                    setSelectedModuleType(value);
                  }}
                />
              </div>
            </>
          )}
      </div>

      <div className="gap-x-2">
        <Button
          isLoading={isLoading}
          loaderSize="small"
          disabled={isLoading}
          className="text-white w-full bg-[#8051FF] mt-4"
          onClick={handleReportSubmit}
        >
          Submit
        </Button>
      </div>
    </div>
  );
};

export default ProductReportFilters;